// ===============================================
// 📊 USER MONITORING DASHBOARD - COMPLETE
// ===============================================
// File: src/pages/Monitoring.tsx

import { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { 
  Activity, Users, Filter, Download, 
  RefreshCw, Search, Eye, TrendingUp,
  Calendar, FileSpreadsheet, FileText,
  BarChart3, PieChart, Clock
} from 'lucide-react';
import { monitoringService, UserActivity, RegisteredUser } from '../services/monitoringApiService';
import { useToast } from '../hooks/useToast';
import {
  LineChart, Line, BarChart, Bar, PieChart as RePieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

export default function Monitoring() {
  const { user } = useAuthStore();
  const { success, error } = useToast();

  // Estados
  const [activeTab, setActiveTab] = useState<'activities' | 'users' | 'charts'>('activities');
  const [activities, setActivities] = useState<UserActivity[]>([]);
  const [users, setUsers] = useState<RegisteredUser[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [stats, setStats] = useState<any>(null);

  // Paginación
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 50;

  // Filtros avanzados
  const [filters, setFilters] = useState({
    status: 'active',
    role: '',
    activity_type: '',
    search: '',
    date_from: '',
    date_to: ''
  });

  // ===============================================
  // 🔄 CARGAR DATOS
  // ===============================================
  const loadActivities = async () => {
    setLoading(true);
    try {
      const response = await monitoringService.getUserActivities({
        page,
        limit,
        activity_type: filters.activity_type || undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined
      });
      
      setActivities(response.data);
      setTotalPages(response.pagination.totalPages);
      setTotal(response.pagination.total);
    } catch (err) {
      error('Error al cargar actividades');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await monitoringService.getRegisteredUsers({
        page,
        limit,
        status: filters.status || undefined,
        role: filters.role || undefined,
        search: filters.search || undefined
      });
      
      setUsers(response.data);
      setTotalPages(response.pagination.totalPages);
      setTotal(response.pagination.total);
      setStats(response.stats);
    } catch (err) {
      error('Error al cargar usuarios');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await monitoringService.getActivitySummary();
      setSummary(response.data);
    } catch (err) {
      console.error('Error loading summary:', err);
    }
  };

  // ===============================================
  // 📥 EXPORTACIÓN
  // ===============================================
  const handleExport = async (format: 'excel' | 'csv') => {
    setExporting(true);
    try {
      if (activeTab === 'activities') {
        await monitoringService.exportActivities(format, {
          activity_type: filters.activity_type || undefined,
          date_from: filters.date_from || undefined,
          date_to: filters.date_to || undefined
        });
        success(`Actividades exportadas en formato ${format.toUpperCase()}`);
      } else if (activeTab === 'users') {
        await monitoringService.exportUsers(format, {
          status: filters.status || undefined,
          role: filters.role || undefined,
          search: filters.search || undefined
        });
        success(`Usuarios exportados en formato ${format.toUpperCase()}`);
      }
    } catch (err) {
      error('Error al exportar datos');
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  // ===============================================
  // 🎯 EFFECTS
  // ===============================================
  useEffect(() => {
    if (activeTab === 'activities') {
      loadActivities();
    } else if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'charts') {
      loadSummary();
    }
  }, [activeTab, page]); // Removemos filters de aquí

  // Efecto separado para filtros con debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1); // Reset a página 1 cuando cambian filtros
      if (activeTab === 'activities') {
        loadActivities();
      } else if (activeTab === 'users') {
        loadUsers();
      }
    }, 300); // Debounce de 300ms

    return () => clearTimeout(timer);
  }, [filters]);

  // ===============================================
  // 🎨 RENDER HELPERS
  // ===============================================
  const getActivityIcon = (type: string) => {
    const icons: Record<string, string> = {
      login: '🔐',
      logout: '🚪',
      token_refresh: '🔄',
      password_change: '🔑',
      profile_update: '✏️',
      api_call: '📡'
    };
    return icons[type] || '📝';
  };

  const getRoleBadge = (role: string) => {
    const colors: Record<string, string> = {
      admin: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      technician: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      user: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
    };
    
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors[role] || colors.user}`}>
        {role}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      inactive: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
      suspended: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
    };
    
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors[status] || colors.inactive}`}>
        {status}
      </span>
    );
  };

  // ===============================================
  // 📊 RENDER STATS
  // ===============================================
  const renderStats = () => {
    if (!stats) return null;

    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Usuarios</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.total_users}
              </p>
            </div>
            <Users className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Usuarios Activos</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {stats.active_users}
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Suscripciones Activas</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {stats.active_subscriptions}
              </p>
            </div>
            <Activity className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Usuarios Premium</p>
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {stats.premium_users}
              </p>
            </div>
            <Eye className="w-8 h-8 text-purple-500" />
          </div>
        </div>
      </div>
    );
  };

  // ===============================================
  // 📈 RENDER CHARTS
  // ===============================================
  const renderCharts = () => {
    if (!summary) return <div className="text-center py-12">Cargando gráficos...</div>;

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

    return (
      <div className="space-y-6">
        {/* Actividad por tipo */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
            <PieChart className="w-5 h-5" />
            Actividades por Tipo (Últimas 24h)
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <RePieChart>
              <Pie
                data={summary.activity_by_type}
                dataKey="count"
                nameKey="activity_type"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={(entry) => `${entry.activity_type}: ${entry.count}`}
              >
                {summary.activity_by_type.map((entry: any, index: number) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </RePieChart>
          </ResponsiveContainer>
        </div>

        {/* Actividad por hora */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Actividad por Hora (Últimas 24h)
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={summary.hourly_activity}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="hour" 
                tickFormatter={(value) => new Date(value).getHours() + ':00'}
              />
              <YAxis />
              <Tooltip 
                labelFormatter={(value) => new Date(value).toLocaleString('es-CL')}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="count" 
                stroke="#8884d8" 
                name="Actividades"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Top usuarios */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Top 10 Usuarios Más Activos (Últimas 24h)
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={summary.top_users}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="activity_count" fill="#82ca9d" name="Actividades" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Top ubicaciones */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
            Top 10 Ubicaciones (Últimas 24h)
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b dark:border-gray-700">
                  <th className="text-left py-2 text-gray-600 dark:text-gray-300">País</th>
                  <th className="text-left py-2 text-gray-600 dark:text-gray-300">Ciudad</th>
                  <th className="text-right py-2 text-gray-600 dark:text-gray-300">Actividades</th>
                </tr>
              </thead>
              <tbody>
                {summary.top_locations.map((loc: any, index: number) => (
                  <tr key={index} className="border-b dark:border-gray-700">
                    <td className="py-2 text-gray-900 dark:text-white">{loc.country_code}</td>
                    <td className="py-2 text-gray-700 dark:text-gray-300">{loc.city || 'N/A'}</td>
                    <td className="py-2 text-right font-semibold text-gray-900 dark:text-white">
                      {loc.count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ===============================================
  // 📋 RENDER ACTIVITIES TABLE
  // ===============================================
  const renderActivitiesTable = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Usuario
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Actividad
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Ubicación
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              IP
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Fecha
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
          {activities.map((activity) => (
            <tr key={activity.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
              <td className="px-6 py-4 whitespace-nowrap">
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {activity.user_name}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {activity.user_email}
                  </div>
                  {getRoleBadge(activity.user_role)}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                  activity.activity_type === 'login' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                  activity.activity_type === 'logout' ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' :
                  activity.activity_type === 'api_call' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                  activity.activity_type === 'profile_update' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                  activity.activity_type === 'password_change' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                  'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                }`}>
                  <span>{getActivityIcon(activity.activity_type)}</span>
                  <span>{activity.activity_type}</span>
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                {activity.city || '-'}, {activity.country_code || '-'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                {activity.ip_address}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                {new Date(activity.created_at).toLocaleString('es-CL')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // ===============================================
  // 👥 RENDER USERS TABLE
  // ===============================================
  const renderUsersTable = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Usuario
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Rol
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Estado
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Suscripción
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Queries
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Último Login
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
          {users.map((user) => (
            <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
              <td className="px-6 py-4">
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {user.name}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {user.email}
                  </div>
                  {user.company && (
                    <div className="text-xs text-gray-400">{user.company}</div>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {getRoleBadge(user.role)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {getStatusBadge(user.status)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900 dark:text-white">
                  {user.subscription_type}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {user.subscription_status}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                {user.daily_queries_used} / {user.daily_queries_limit}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                {user.last_login ? new Date(user.last_login).toLocaleDateString('es-CL') : 'Nunca'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // ===============================================
  // 🎨 MAIN RENDER
  // ===============================================
  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Monitoreo de Sistema
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Panel de control para administradores y técnicos
        </p>
      </div>

      {/* Stats */}
      {activeTab === 'users' && renderStats()}

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex -mb-px">
            <button
              onClick={() => { setActiveTab('activities'); setPage(1); }}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'activities'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Activity className="w-5 h-5 inline mr-2" />
              Actividades
            </button>
            <button
              onClick={() => { setActiveTab('users'); setPage(1); }}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'users'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Users className="w-5 h-5 inline mr-2" />
              Usuarios
            </button>
            <button
              onClick={() => { setActiveTab('charts'); loadSummary(); }}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'charts'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <BarChart3 className="w-5 h-5 inline mr-2" />
              Gráficos
            </button>
          </nav>
        </div>

        {/* Filtros y exportación */}
        {activeTab !== 'charts' && (
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex gap-4 flex-wrap items-center">
            {/* Filtros */}
            {activeTab === 'users' && (
              <>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="all">Todos los estados</option>
                  <option value="active">Activo</option>
                  <option value="inactive">Inactivo</option>
                  <option value="suspended">Suspendido</option>
                  <option value="pending">Pendiente</option>
                </select>

                <select
                  value={filters.role}
                  onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Todos los roles</option>
                  <option value="admin">Admin</option>
                  <option value="technician">Technician</option>
                  <option value="user">User</option>
                </select>

                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar por nombre o email..."
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
              </>
            )}

            {activeTab === 'activities' && (
              <>
                <select
                  value={filters.activity_type}
                  onChange={(e) => setFilters({ ...filters, activity_type: e.target.value })}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Todos los tipos</option>
                  <option value="login">Login</option>
                  <option value="logout">Logout</option>
                  <option value="token_refresh">Token Refresh</option>
                  <option value="password_change">Password Change</option>
                  <option value="profile_update">Profile Update</option>
                  <option value="api_call">API Call</option>
                </select>

                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <span className="text-gray-500">a</span>
                  <input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </>
            )}

            {/* Botones de acción */}
            <div className="flex gap-2 ml-auto">
              <button
                onClick={() => activeTab === 'activities' ? loadActivities() : loadUsers()}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </button>

              <div className="relative group">
                <button
                  disabled={exporting}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  Exportar
                </button>
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                  <button
                    onClick={() => handleExport('excel')}
                    disabled={exporting}
                    className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center gap-2 rounded-t-lg"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    Exportar Excel
                  </button>
                  <button
                    onClick={() => handleExport('csv')}
                    disabled={exporting}
                    className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center gap-2 rounded-b-lg"
                  >
                    <FileText className="w-4 h-4" />
                    Exportar CSV
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-4">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : (
            <>
              {activeTab === 'activities' && renderActivitiesTable()}
              {activeTab === 'users' && renderUsersTable()}
              {activeTab === 'charts' && renderCharts()}

              {/* Pagination */}
              {activeTab !== 'charts' && (
                <div className="mt-6 flex items-center justify-between">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Mostrando {((page - 1) * limit) + 1} - {Math.min(page * limit, total)} de {total}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                    >
                      Anterior
                    </button>
                    <span className="px-4 py-2 text-gray-700 dark:text-gray-300">
                      Página {page} de {totalPages}
                    </span>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}